import React from 'react';
import _ from 'lodash';

import {
  styled,
  Box,
  IconButton,
  Link,
  Grid
} from '@mui/material';
import {
  fields,
  LoadingOverlay,
  modalUtil,
  Table
} from 'iron-forge-react-modules';
import {
  ApprovalTypeChip,
  PermissionWall,
  ReceiveReturnAuthorizationModal,
  routeDirectory
} from 'modules';
import {
  Approval,
  Autorenew,
  Check,
  DoDisturb,
  Receipt
} from '@mui/icons-material';

import * as dataService from './dataService';
import * as types from './types';



const TableComponent = Table.genTableComponent<types.IColumnId>();
const LineItemTableComponent = Table.genTableComponent<types.ILineItemColumnId>();



const FormContainerBox = styled(Box)(({ theme }) => ({
  margin: theme.spacing(2)
}));

export interface IProps {
  storeId: string;
}

export const FC: React.FC<IProps> = props => {
  const {
    pageInfo,
    queryTuple,
    fnQueryPage,
    fnQuery
  } = dataService.useQuery(props.storeId);
  const {
    modalType2IsOpen,
    setModalIsOpen
  } = modalUtil.useModalTrackers(['receiveItems']);
  const [selectedReturnAuthorizationId, setSelectedReturnAuthorizationId] = React.useState('');
  const [approveReturnAuthorization, approveReturnAuthorizationTuple] = dataService.useApproveReturnAuthorization();
  const [after, setAfter] = React.useState<Date | null>(null);
  const [before, setBefore] = React.useState<Date | null>(null);



  const processSubmit = React.useCallback(
    (after: Date | null, before: Date | null) => {
      fnQuery({
        filter: {
          after,
          before
        }
      });
    },
    // This is disabled here because the fnWrappedQuery inside of `useListQuery` appears to generate a new reference
    // each render. This causes the `useCallback` hook to render again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const debouncedProcessSubmit = React.useMemo(
    () => _.debounce(processSubmit, 1000),
    [processSubmit]
  );

  React.useEffect(
    () => {
      if (before || after) {
        debouncedProcessSubmit(after, before);
      }
    },
    [
      after,
      before,
      debouncedProcessSubmit
    ]
  );



  const refetchQuery = React.useCallback(
    () => {
      if (queryTuple.refetch) {
        queryTuple.refetch();
      }
    },
    [queryTuple]
  );



  const handleClickDenyAuthorization = React.useCallback(
    async (returnAuthorizationId: string) => {
      if (window.confirm("Are you sure you want to deny this return?")) {
        await approveReturnAuthorization(returnAuthorizationId, 'denied');
        refetchQuery();
      }
    },
    [
      approveReturnAuthorization,
      refetchQuery
    ]
  );

  const handleClickApproveAuthorization = React.useCallback(
    async (returnAuthorizationId: string) => {
      if (window.confirm("Are you sure you want to approve this return?")) {
        await approveReturnAuthorization(returnAuthorizationId, 'approved');
        refetchQuery();
      }
    },
    [
      approveReturnAuthorization,
      refetchQuery
    ]
  );

  const handleClickReceiveItems = React.useCallback(
    (returnAuthorizationId: string) => {
      setSelectedReturnAuthorizationId(returnAuthorizationId);
      setModalIsOpen('receiveItems', true);
    },
    [setModalIsOpen]
  );

  const handleReceiveItems = React.useCallback(
    () => {
      setModalIsOpen('receiveItems', false);
      refetchQuery();
    },
    [
      setModalIsOpen,
      refetchQuery
    ]
  );

  const handleCloseReceiveReturnAuthorizationModal = React.useCallback(
    () => {
      setModalIsOpen('receiveItems', false);
    },
    [setModalIsOpen]
  );



  const rows = React.useMemo(
    () => _.map(queryTuple.data.returnAuthorizations ?? [], (returnAuthorization): types.IRow => ({
      id: returnAuthorization.id,
      expandedContent: (
        <>
          <LineItemTableComponent
            disableSorting
            rows={_.map(returnAuthorization.lineItems, (lineItem): types.ILineItemRow => ({
              id: lineItem.id,
              columnId2Value: {
                productName: {
                  type: 'string',
                  label: lineItem.productName
                },
                quantity: {
                  type: 'string',
                  label: lineItem.quantity.toString()
                },
                reasonForReturn: {
                  type: 'string',
                  label: lineItem.reasonForReturn
                },
                sku: {
                  type: 'string',
                  label: lineItem.sku
                }
              }
            }))}
            columns={[
              {
                id: 'sku',
                label: "SKU"
              },
              {
                id: 'productName',
                label: "Product Name"
              },
              {
                id: 'quantity',
                label: "Quantity"
              },
              {
                id: 'reasonForReturn',
                label: "Reason for Return"
              }
            ]} />
        </>
      ),
      columnId2Value: {
        returnDate: {
          type: 'string',
          label: returnAuthorization.returnDate.toFormat('yyyy-MM-dd @ hh:mm a')
        },
        returnNumber: {
          type: 'component',
          sortableValue: returnAuthorization.returnNumber,
          label: (
            <Link
              target="_blank"
              href={routeDirectory.partner.storeDetail.path(props.storeId ?? '', returnAuthorization.orderId)}>
              {returnAuthorization.returnNumber}
            </Link>
          )
        },
        approvalStatus: {
          type: 'component',
          sortableValue: returnAuthorization.approvalStatus,
          label: returnAuthorization.approvalStatus !== 'pending' ? (
            <ApprovalTypeChip.FC approvalTypeName={returnAuthorization.approvalStatus} />
          ) : (
            <PermissionWall.FC
              requiresPermissions={['returnAuthorization_approval']}
              storeId={props.storeId}>
              <IconButton
                color="error"
                onClick={_.partial(handleClickDenyAuthorization, returnAuthorization.id)}>
                <DoDisturb />
              </IconButton>

              <IconButton
                color="success"
                onClick={_.partial(handleClickApproveAuthorization, returnAuthorization.id)}>
                <Approval />
              </IconButton>
            </PermissionWall.FC>
          )
        },
        acceptanceStatus: {
          type: 'component',
          sortableValue: returnAuthorization.approvalStatus,
          label: <ApprovalTypeChip.FC approvalTypeName={returnAuthorization.acceptanceStatus} />
        },
        itemsReceived: {
          type: 'component',
          sortableValue: returnAuthorization.itemsReceived,
          label: (returnAuthorization.itemsReceived > -1 ? // This is intended to reflect not received
            <Check color="success" /> : (
              <PermissionWall.FC
                requiresPermissions={['itemReceipt_create']}
                storeId={props.storeId}>
                <IconButton
                  disabled={returnAuthorization.approvalStatus !== 'approved'}
                  onClick={_.partial(handleClickReceiveItems, returnAuthorization.id)}>
                  {returnAuthorization.approvalStatus !== 'approved' ? <Autorenew /> : <Receipt />}
                </IconButton>
              </PermissionWall.FC>
            )
          )
        },
        refundStatus: {
          type: 'component',
          sortableValue: returnAuthorization.approvalStatus,
          label: <ApprovalTypeChip.FC approvalTypeName={returnAuthorization.refundStatus} />
        }
      }
    })),
    [
      handleClickApproveAuthorization,
      handleClickDenyAuthorization,
      handleClickReceiveItems,
      props.storeId,
      queryTuple.data.returnAuthorizations
    ]
  );



  const loading = React.useMemo(
    () => _.some([
      queryTuple,
      approveReturnAuthorizationTuple
    ], tuple => tuple.loading),
    [
      queryTuple,
      approveReturnAuthorizationTuple
    ]
  );

  return (
    <>
      <LoadingOverlay.FC active={loading} />

      <FormContainerBox>
        <Grid
          container
          spacing={1}>
          <Grid
            item
            xs={2}>
            <fields.DateField.FC
              isDisabled={queryTuple.loading}
              label="After"
              value={after}
              onChange={setAfter} />
          </Grid>

          <Grid
            item
            xs={2}>
            <fields.DateField.FC
              isDisabled={queryTuple.loading}
              label="Before"
              value={before}
              onChange={setBefore} />
          </Grid>
        </Grid>
      </FormContainerBox>

      <TableComponent
        disableSorting
        paginationOptions={Table.pageInfo2PaginationOptions(pageInfo, fnQueryPage)}
        rows={rows}
        initSortOptions={{
          columnId: 'returnDate',
          direction: 'descending'
        }}
        columns={[
          {
            id: 'returnNumber',
            label: "Return Number"
          },
          {
            id: 'returnDate',
            label: "Return Date"
          },
          {
            id: 'approvalStatus',
            label: "Authorization Approval"
          },
          {
            id: 'itemsReceived',
            label: "Is Received?"
          },
          {
            id: 'acceptanceStatus',
            label: "Return Accepted?"
          },
          {
            id: 'refundStatus',
            label: "Refund?"
          }
        ]}
      />

      {modalType2IsOpen.receiveItems && selectedReturnAuthorizationId && (
        <ReceiveReturnAuthorizationModal.FC
          returnAuthorizationId={selectedReturnAuthorizationId}
          onReceiveItems={handleReceiveItems}
          onClose={handleCloseReceiveReturnAuthorizationModal} />
      )}
    </>
  );
};
